const MIN_WIDGET_PX = 280;
const SIDE_BY_SIDE_TOP_SLACK_PX = 48;
const EXTRA_HEIGHT_PX = 144;
const ENFOLD_STACK_BELOW_PX = 768;

interface ColumnLayout {
  column: HTMLElement;
  sibling: HTMLElement;
}

export class WidgetHeightLock {
  private observer: ResizeObserver | null = null;
  private observedLayout: ColumnLayout | null = null;

  constructor(private readonly mount: HTMLElement) {}

  attach(): void {
    this.observer = new ResizeObserver(this.sync);
    this.observer.observe(this.mount);
    window.addEventListener("resize", this.sync);
    requestAnimationFrame(this.sync);
  }

  detach(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.observedLayout = null;
    window.removeEventListener("resize", this.sync);
    this.mount.style.removeProperty("--nv-widget-height");
  }

  private sync = (): void => {
    if (typeof window !== "undefined" && !WidgetHeightLock.shouldLockHeight(window.innerWidth)) {
      this.replaceLayoutObservations(null);
      this.apply(null);
      return;
    }
    const layout = this.findLayout();
    this.replaceLayoutObservations(layout);
    this.apply(layout);
  };

  private replaceLayoutObservations(next: ColumnLayout | null): void {
    if (!this.observer) return;
    const prev = this.observedLayout;
    if (prev && prev !== next) {
      if (!next || prev.sibling !== next.sibling) this.observer.unobserve(prev.sibling);
      if (!next || prev.column !== next.column) this.observer.unobserve(prev.column);
    }
    if (next) {
      this.observer.observe(next.sibling);
      this.observer.observe(next.column);
    }
    this.observedLayout = next;
  }

  private apply(layout: ColumnLayout | null): void {
    const phone =
      typeof window !== "undefined" && !WidgetHeightLock.shouldLockHeight(window.innerWidth);
    if (phone || !layout || !WidgetHeightLock.areSideBySide(layout.column, layout.sibling)) {
      this.mount.style.removeProperty("--nv-widget-height");
      return;
    }

    const siblingHeight = WidgetHeightLock.contentHeight(layout.sibling);
    const offset =
      this.mount.getBoundingClientRect().top - layout.column.getBoundingClientRect().top;
    const padBottom = Number.parseFloat(getComputedStyle(layout.column).paddingBottom) || 0;
    const px = WidgetHeightLock.targetHeightPx(siblingHeight, offset, padBottom);
    const next = `${px}px`;
    if (this.mount.style.getPropertyValue("--nv-widget-height") === next) return;
    this.mount.style.setProperty("--nv-widget-height", next);
  }

  private findLayout(): ColumnLayout | null {
    let column = this.mount.parentElement;
    while (column && column !== document.body) {
      const row = column.parentElement;
      if (row) {
        const sibling = WidgetHeightLock.siblingColumn(row, column);
        if (sibling && WidgetHeightLock.areSideBySide(column, sibling)) {
          return { column, sibling };
        }
      }
      column = column.parentElement;
    }
    return null;
  }

  private static siblingColumn(row: HTMLElement, column: HTMLElement): HTMLElement | null {
    const candidates = Array.from(row.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement && el !== column && el.offsetWidth > 80,
    );
    return candidates.find((el) => WidgetHeightLock.areSideBySide(column, el)) ?? null;
  }

  static shouldLockHeight(viewportPx: number): boolean {
    return viewportPx >= ENFOLD_STACK_BELOW_PX;
  }

  static targetHeightPx(siblingHeight: number, offset: number, padBottom: number): number {
    return Math.max(MIN_WIDGET_PX, Math.round(siblingHeight - offset - padBottom + EXTRA_HEIGHT_PX));
  }

  static areSideBySide(column: HTMLElement, sibling: HTMLElement): boolean {
    const a = column.getBoundingClientRect();
    const b = sibling.getBoundingClientRect();
    return Math.abs(a.top - b.top) < SIDE_BY_SIDE_TOP_SLACK_PX && Math.abs(a.left - b.left) > 1;
  }

  private static contentHeight(el: HTMLElement): number {
    const kids = Array.from(el.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    const style = getComputedStyle(el);
    const padTop = Number.parseFloat(style.paddingTop) || 0;
    const padBottom = Number.parseFloat(style.paddingBottom) || 0;
    if (kids.length === 0) return Math.round(el.scrollHeight);

    const first = kids[0].getBoundingClientRect();
    const last = kids[kids.length - 1].getBoundingClientRect();
    return Math.round(last.bottom - first.top + padTop + padBottom);
  }
}
